import * as THREE from "three";

import { latLngToSphere } from "./projections";

export const FILL_TEX_W = 4096;
export const FILL_TEX_H = 2048;

let _stripePattern = null;
function getStripePattern(ctx) {
    if (_stripePattern) return _stripePattern;
    const size = 12;
    const pCanvas = document.createElement("canvas");
    pCanvas.width = size;
    pCanvas.height = size;
    const pCtx = pCanvas.getContext("2d");
    pCtx.strokeStyle = "#1a1a1a";
    pCtx.lineWidth = 1;
    pCtx.beginPath();
    pCtx.moveTo(0, size);
    pCtx.lineTo(size, 0);
    pCtx.stroke();
    pCtx.beginPath();
    pCtx.moveTo(-size / 2, size / 2);
    pCtx.lineTo(size / 2, -size / 2);
    pCtx.stroke();
    pCtx.beginPath();
    pCtx.moveTo(size / 2, size + size / 2);
    pCtx.lineTo(size + size / 2, size / 2);
    pCtx.stroke();
    _stripePattern = ctx.createPattern(pCanvas, "repeat");
    return _stripePattern;
}

export const computeBBox = (rings) => {
    let minLng = Infinity,
        minLat = Infinity,
        maxLng = -Infinity,
        maxLat = -Infinity;
    for (const ring of rings) {
        for (const [lng, lat] of ring) {
            if (lng < minLng) minLng = lng;
            if (lat < minLat) minLat = lat;
            if (lng > maxLng) maxLng = lng;
            if (lat > maxLat) maxLat = lat;
        }
    }
    return [minLng, minLat, maxLng, maxLat];
};

export const buildMorphableCountryLines = (coordinates, radius) => {
    const results = [];
    const r = radius * 1.002;

    for (const ring of coordinates) {
        const segments = [];
        let current = [];

        for (const [lng, lat] of ring) {
            if (current.length > 0) {
                const prevLng = current[current.length - 1][0];
                if (Math.abs(lng - prevLng) > 180) {
                    if (current.length > 1) segments.push(current);
                    current = [];
                }
            }
            current.push([lng, lat]);
        }
        if (current.length > 1) segments.push(current);

        for (const segment of segments) {
            const positions = [];
            const latLngs = [];

            for (const [lng, lat] of segment) {
                const sp = latLngToSphere(lat, lng, r);
                positions.push(sp[0], sp[1], sp[2]);
                latLngs.push([lat, lng]);
            }

            const geo = new THREE.BufferGeometry();
            geo.setAttribute(
                "position",
                new THREE.Float32BufferAttribute(
                    new Float32Array(positions),
                    3,
                ),
            );
            results.push({ geometry: geo, latLngs, radius: r });
        }
    }

    return results;
};

export const buildGraticuleLines = (radius) => {
    const result = [];
    const r = radius * 0.999;

    for (let lat = -80; lat <= 80; lat += 20) {
        const positions = [];
        const latLngs = [];
        for (let lng = -180; lng <= 180; lng += 2) {
            const sp = latLngToSphere(lat, lng, r);
            positions.push(sp[0], sp[1], sp[2]);
            latLngs.push([lat, lng]);
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(new Float32Array(positions), 3),
        );
        result.push({ geometry: geo, latLngs, radius: r });
    }

    for (let lng = -180; lng < 180; lng += 30) {
        const positions = [];
        const latLngs = [];
        for (let lat = -85; lat <= 85; lat += 2) {
            const sp = latLngToSphere(lat, lng, r);
            positions.push(sp[0], sp[1], sp[2]);
            latLngs.push([lat, lng]);
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute(
            "position",
            new THREE.Float32BufferAttribute(new Float32Array(positions), 3),
        );
        result.push({ geometry: geo, latLngs, radius: r });
    }

    return result;
};

export const buildSphere = (radius, widthSeg, heightSeg) => {
    const vertexLatLng = [];
    const positions = [];
    const indices = [];
    const uvs = [];

    for (let row = 0; row <= heightSeg; row++) {
        const lat = 90 - (row / heightSeg) * 180;
        const v = 1.0 - row / heightSeg;

        for (let col = 0; col <= widthSeg; col++) {
            const lng = (col / widthSeg) * 360 - 180;
            vertexLatLng.push([lat, lng]);

            const sp = latLngToSphere(lat, lng, radius);
            positions.push(sp[0], sp[1], sp[2]);

            const u = col / widthSeg;

            uvs.push(u, v);
        }
    }

    for (let row = 0; row < heightSeg; row++) {
        for (let col = 0; col < widthSeg; col++) {
            const a = row * (widthSeg + 1) + col;
            const b = a + widthSeg + 1;
            indices.push(a, b, a + 1, b, b + 1, a + 1);
        }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(positions, 3),
    );

    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return { geometry, vertexLatLng, radius };
};

export const buildFillSphere = (radius, widthSeg, heightSeg) => {
    const vertexLatLng = [];
    const positions = [];
    const uvs = [];
    const indices = [];

    for (let row = 0; row <= heightSeg; row++) {
        const v = row / heightSeg;
        const lat = 90 - v * 180;
        for (let col = 0; col <= widthSeg; col++) {
            const u = col / widthSeg;
            const lng = u * 360 - 180;
            vertexLatLng.push([lat, lng]);
            const sp = latLngToSphere(lat, lng, radius);
            positions.push(sp[0], sp[1], sp[2]);
            uvs.push(u, 1 - v);
        }
    }

    for (let row = 0; row < heightSeg; row++) {
        for (let col = 0; col < widthSeg; col++) {
            const a = row * (widthSeg + 1) + col;
            const b = a + widthSeg + 1;
            indices.push(a, b, a + 1, b, b + 1, a + 1);
        }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(positions, 3),
    );
    geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);
    geometry.computeVertexNormals();

    return { geometry, vertexLatLng };
};

export const drawCountryFill = (ctx, geometry, locked) => {
    ctx.clearRect(0, 0, FILL_TEX_W, FILL_TEX_H);

    const polygons =
        geometry.type === "Polygon"
            ? [geometry.coordinates]
            : geometry.coordinates;

    if (locked) {
        ctx.fillStyle = getStripePattern(ctx);
    } else {
        ctx.fillStyle = "#ffffff";
    }

    for (const polygon of polygons) {
        const rawRing = polygon[0];
        if (!rawRing || rawRing.length < 3) continue;

        let crossingIdx = -1;

        for (let i = 1; i < rawRing.length; i++) {
            if (Math.abs(rawRing[i][0] - rawRing[i - 1][0]) > 180) {
                crossingIdx = i;
                break;
            }
        }

        const unwrap = (ring) => {
            const result = [ring[0]];
            for (let i = 1; i < ring.length; i++) {
                let lng = ring[i][0];
                const prev = result[i - 1][0];
                while (lng - prev > 180) lng -= 360;
                while (lng - prev < -180) lng += 360;
                result.push([lng, ring[i][1]]);
            }
            return result;
        };

        let outerRing = unwrap(rawRing);

        const lngs = outerRing.map((v) => v[0]);
        const lngRange = Math.max(...lngs) - Math.min(...lngs);
        const isPolar = lngRange > 300;
        let poleY = 0;

        if (isPolar && crossingIdx >= 0) {
            const rotated = [
                ...rawRing.slice(crossingIdx),
                ...rawRing.slice(0, crossingIdx),
            ];
            outerRing = unwrap(rotated);

            const avgLat =
                outerRing.reduce((s, v) => s + v[1], 0) / outerRing.length;
            const poleLat = avgLat < 0 ? -89.5 : 89.5;
            poleY = ((90 - poleLat) / 180) * FILL_TEX_H;
        }

        for (const lngOffset of [0, -360, 360]) {
            ctx.beginPath();
            for (let i = 0; i < outerRing.length; i++) {
                const lng = outerRing[i][0] + lngOffset;
                const lat = Math.max(-89.5, Math.min(89.5, outerRing[i][1]));

                const x = ((lng + 180) / 360) * FILL_TEX_W;
                const y = ((90 - lat) / 180) * FILL_TEX_H;

                if (i === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            }

            if (isPolar) {
                const lastX =
                    ((outerRing[outerRing.length - 1][0] + lngOffset + 180) /
                        360) *
                    FILL_TEX_W;
                const firstX =
                    ((outerRing[0][0] + lngOffset + 180) / 360) * FILL_TEX_W;
                ctx.lineTo(lastX, poleY);
                ctx.lineTo(firstX, poleY);
            }

            ctx.closePath();
            ctx.fill("evenodd");
        }
    }
};

export const pointInRing = (testLat, testLng, ring) => {
    let inside = false;

    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [lngI, latI] = ring[i];
        const [lngJ, latJ] = ring[j];

        if (Math.abs(lngI - lngJ) > 180) continue;

        const intersect =
            latI > testLat !== latJ > testLat &&
            testLng < ((lngJ - lngI) * (testLat - latI)) / (latJ - latI) + lngI;

        if (intersect) inside = !inside;
    }
    return inside;
};

export const findCountryAtLatLng = (lat, lng, features) => {
    for (const f of features) {
        const [minLng, minLat, maxLng, maxLat] = f.bbox;
        if (lng < minLng || lng > maxLng || lat < minLat || lat > maxLat)
            continue;

        if (f.type === "Polygon") {
            if (pointInRing(lat, lng, f.coordinates[0])) return f.code;
        } else if (f.type === "MultiPolygon") {
            for (const polygon of f.coordinates) {
                if (pointInRing(lat, lng, polygon[0])) return f.code;
            }
        }
    }
    return null;
};
